/**
 * Talk wallet recharge checkout — sandbox capture or Razorpay Checkout (production).
 * Mirrors carePassPayment.js's pattern.
 */

import api from "./api";
import { showToast } from "../utils/toast";

function loadRazorpayScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("Browser only"));
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Failed to load Razorpay")));
      if (window.Razorpay) resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Razorpay checkout"));
    document.body.appendChild(script);
  });
}

/**
 * Recharge the talk wallet (paid minutes + call credits). Resolves when sandbox
 * capture completes or Razorpay checkout succeeds.
 */
export async function payTalkWalletRecharge() {
  const orderRes = await api.createTalkWalletRechargeOrder();
  const payload = orderRes?.data || orderRes;

  if (payload?.mockAllowed) {
    return {
      mockCompleted: Boolean(payload?.mockCompleted),
      quota: payload?.quota || null,
      transaction: payload?.transaction || null,
    };
  }

  const order = payload?.order;
  const keyId = payload?.keyId;
  if (!order?.id || !keyId) {
    throw new Error("Payment order could not be created. Try again or contact support.");
  }

  await loadRazorpayScript();

  const razorpay = await new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: keyId,
      amount: order.amount,
      currency: order.currency || "INR",
      name: "NemaCare",
      description: "Talk wallet recharge",
      order_id: order.id,
      prefill: payload?.prefill || undefined,
      handler(response) {
        showToast.success("Payment received. Adding your call time…");
        resolve(response);
      },
      modal: {
        ondismiss() {
          reject(new Error("Payment cancelled"));
        },
      },
    });
    rzp.on("payment.failed", (err) => {
      reject(new Error(err?.error?.description || "Payment failed"));
    });
    rzp.open();
  });

  const confirmRes = await api.confirmTalkWalletRechargePayment({
    razorpay_order_id: razorpay?.razorpay_order_id || order.id,
    razorpay_payment_id: razorpay?.razorpay_payment_id,
    razorpay_signature: razorpay?.razorpay_signature,
  });

  return {
    razorpay,
    quota: confirmRes?.quota || null,
    transaction: confirmRes?.transaction || null,
  };
}
